import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Image as ImageIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";
import AdminPagination from "@/components/admin/AdminPagination";

export default function AdminPosts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [deleteId, setDeleteId] = useState(null);

  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    post_type: searchParams.get("type") || "",
    status: searchParams.get("status") || "",
  });

  const [limit, setLimit] = useState(25);

  useEffect(() => {
    fetchPosts();
  }, [page, limit, filters]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append("search", filters.search);
      if (filters.post_type) params.append("post_type", filters.post_type);
      if (filters.status) params.append("status", filters.status);
      params.append("skip", (page - 1) * limit);
      params.append("limit", limit);

      // Use token from localStorage (axios interceptor adds it automatically)
      const response = await axios.get(`${API}/posts?${params}`);
      setPosts(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching posts:", error);
      const errorMsg = error.response?.data?.detail || error.response?.statusText || error.message || "Error desconocido";
      toast.error(`Error al cargar los posts: ${errorMsg}`);
      setPosts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    const params = new URLSearchParams();
    if (filters.search) params.set("search", filters.search);
    if (filters.post_type) params.set("type", filters.post_type);
    if (filters.status) params.set("status", filters.status);
    setSearchParams(params);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/posts/${deleteId}`);
      toast.success("Post eliminado");
      fetchPosts();
    } catch (error) {
      toast.error("Error al eliminar");
    }
    setDeleteId(null);
  };

  const typeLabels = {
    news: { label: "Noticia", color: "bg-blue-100 text-blue-800" },
    destination: { label: "Destino", color: "bg-green-100 text-green-800" },
    experience: { label: "Experiencia", color: "bg-purple-100 text-purple-800" },
    flavor: { label: "Sabor", color: "bg-orange-100 text-orange-800" },
    video: { label: "Video", color: "bg-red-100 text-red-800" },
    page: { label: "Página", color: "bg-gray-100 text-gray-800" },
  };


  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-posts">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-text">
              Posts
            </h1>
            <p className="text-brand-textSecondary mt-1">
              {total} posts en total
            </p>
          </div>
          <Link to="/admin/posts/new">
            <Button className="rounded-full" data-testid="new-post-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Post
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 shadow-card">
          <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar posts..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
                data-testid="posts-search-input"
              />
            </div>
            <Select
              value={filters.post_type || "all"}
              onValueChange={(value) => setFilters({ ...filters, post_type: value === "all" ? "" : value })}
            >
              <SelectTrigger className="w-full lg:w-40" data-testid="type-filter">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="news">Noticias</SelectItem>
                <SelectItem value="destination">Destinos</SelectItem>
                <SelectItem value="experience">Experiencias</SelectItem>
                <SelectItem value="flavor">Sabores</SelectItem>
                <SelectItem value="video">Videos</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.status || "all"}
              onValueChange={(value) => setFilters({ ...filters, status: value === "all" ? "" : value })}
            >
              <SelectTrigger className="w-full lg:w-40" data-testid="status-filter">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="published">Publicado</SelectItem>
                <SelectItem value="draft">Borrador</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" data-testid="filter-btn">
              Filtrar
            </Button>
          </form>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Título</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6}>
                      <div className="h-12 bg-gray-100 animate-pulse rounded" />
                    </TableCell>
                  </TableRow>
                ))
              ) : posts.length > 0 ? (
                posts.map((post) => (
                  <TableRow key={post.post_id} data-testid={`post-row-${post.post_id}`}>
                    <TableCell>
                      {post.featured_image_url ? (
                        <img
                          src={post.featured_image_url}
                          alt=""
                          className="w-10 h-10 rounded object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center">
                          <ImageIcon className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-brand-text line-clamp-1">
                        {post.title}
                      </div>
                      <div className="text-sm text-brand-textMuted">
                        /{post.slug}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={typeLabels[post.post_type]?.color || "bg-gray-100"}>
                        {typeLabels[post.post_type]?.label || post.post_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {post.status === "published" ? (
                        <span className="flex items-center gap-1 text-green-600">
                          <Eye className="h-4 w-4" />
                          Publicado
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-yellow-600">
                          <EyeOff className="h-4 w-4" />
                          Borrador
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-brand-textSecondary">
                      {post.created_at
                        ? new Date(post.created_at).toLocaleDateString("es-HN")
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/admin/posts/${post.post_id}`}>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`edit-post-${post.post_id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteId(post.post_id)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-post-${post.post_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <p className="text-brand-textSecondary">No se encontraron posts</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="posts-pagination"
          label="posts"
        />

        {/* Delete Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar este post?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer. El post será eliminado permanentemente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
